import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import { SingleStoreMap } from "../components/StoreMap";
import LocationPicker from "../components/LocationPicker";
import {
  fetchStoreDetail,
  updateStore,
  selectStoreById,
  selectStoreErrorById,
  selectStoreStatusById,
  selectStoreUpdateError,
  selectStoreUpdateStatus,
} from "../features/stores/storesSlice";
import {
  fetchInventoryByStore,
  selectInventoryByStore,
  selectInventoryCountByStore,
  selectInventoryErrorByStore,
  selectInventoryStatusByStore,
} from "../features/inventory/inventorySlice";
import "./StoreDetailPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatNumber(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString("ru-RU");
}

function initEditData(store) {
  return {
    name: store?.name || "",
    address: store?.address || "",
    latitude: store?.latitude ?? null,
    longitude: store?.longitude ?? null,
    is_active: store?.is_active ?? true,
    is_only_warehouse: store?.is_only_warehouse ?? false,
  };
}

export default function StoreDetailPage() {
  const { storeId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const PAGE_SIZE = 50;

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ordering, setOrdering] = useState("-updated_at");

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const store = useSelector(selectStoreById(storeId));
  const storeStatus = useSelector(selectStoreStatusById(storeId));
  const storeError = useSelector(selectStoreErrorById(storeId));
  const updateStatus = useSelector(selectStoreUpdateStatus(storeId));
  const updateError = useSelector(selectStoreUpdateError(storeId));

  const inventoryItems = useSelector(selectInventoryByStore(storeId));
  const inventoryCount = useSelector(selectInventoryCountByStore(storeId));
  const inventoryStatus = useSelector(selectInventoryStatusByStore(storeId));
  const inventoryError = useSelector(selectInventoryErrorByStore(storeId));

  const searchTrimmed = useMemo(() => search.trim(), [search]);

  useEffect(() => {
    dispatch(fetchStoreDetail({ storeId }));
  }, [dispatch, storeId]);

  useEffect(() => {
    dispatch(
      fetchInventoryByStore({
        storeId,
        search: searchTrimmed,
        page,
        pageSize: PAGE_SIZE,
        ordering,
      })
    );
  }, [dispatch, storeId, searchTrimmed, page, refreshKey, ordering]);

  // Close edit mode after successful save
  useEffect(() => {
    if (updateStatus === "succeeded") {
      setIsEditing(false);
    }
  }, [updateStatus]);

  const totalPages = Math.max(1, Math.ceil((inventoryCount || 0) / PAGE_SIZE));

  useEffect(() => {
    if (inventoryStatus === "succeeded" && page > totalPages) {
      setPage(totalPages);
    }
  }, [inventoryStatus, page, totalPages]);

  const visibleInventory = useMemo(
    () => (Array.isArray(inventoryItems) ? inventoryItems : []),
    [inventoryItems]
  );

  const totalQuantity = useMemo(
    () => visibleInventory.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0),
    [visibleInventory]
  );

  const handleSort = (field) => {
    if (ordering === field) setOrdering(`-${field}`);
    else if (ordering === `-${field}`) setOrdering(field);
    else setOrdering(field);
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return "";
  };

  const handleEditOpen = () => {
    setEditData(initEditData(store));
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const handleEditSave = () => {
    const payload = { ...editData };
    if (payload.latitude === "") payload.latitude = null;
    if (payload.longitude === "") payload.longitude = null;
    dispatch(updateStore({ storeId, data: payload }));
  };

  const setField = (field) => (value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const isSaving = updateStatus === "loading";

  return (
    <div className="store-detail-page">
      <div className="header">
        <div>
          {fromList && (
            <Link to={fromList} className="back-link">
              ← Назад к списку
            </Link>
          )}
          <div className="title">
            {storeStatus === "loading" && "Загрузка…"}
            {storeStatus === "failed" && "Ошибка загрузки склада"}
            {storeStatus === "succeeded" && (store?.name || `Склад #${storeId}`)}
          </div>
        </div>
        {storeStatus === "succeeded" && store && !isEditing && (
          <button className="btn btn-edit" onClick={handleEditOpen}>
            Редактировать
          </button>
        )}
      </div>

      {storeStatus === "loading" && <Loading text="Загрузка информации о складе…" />}
      {storeStatus === "failed" && <div className="error">{storeError}</div>}

      {/* ── EDIT FORM ── */}
      {isEditing && editData && (
        <div className="edit-section">
          <div className="edit-section-title">Редактирование магазина</div>

          {updateError && <div className="edit-error">{updateError}</div>}

          <div className="edit-grid">
            <div className="edit-field edit-field--full">
              <label className="edit-label">Название</label>
              <input
                className="edit-input"
                value={editData.name}
                onChange={(e) => setField("name")(e.target.value)}
                placeholder="Название магазина"
              />
            </div>

            <div className="edit-field edit-field--full">
              <label className="edit-label">Адрес</label>
              <textarea
                className="edit-input edit-textarea"
                value={editData.address}
                onChange={(e) => setField("address")(e.target.value)}
                placeholder="Адрес магазина"
                rows={2}
              />
            </div>

            <div className="edit-field">
              <label className="edit-label">Широта</label>
              <input
                className="edit-input edit-input--mono"
                type="number"
                step="any"
                value={editData.latitude ?? ""}
                onChange={(e) => setField("latitude")(e.target.value === "" ? null : parseFloat(e.target.value))}
                placeholder="42.8746"
              />
            </div>

            <div className="edit-field">
              <label className="edit-label">Долгота</label>
              <input
                className="edit-input edit-input--mono"
                type="number"
                step="any"
                value={editData.longitude ?? ""}
                onChange={(e) => setField("longitude")(e.target.value === "" ? null : parseFloat(e.target.value))}
                placeholder="74.5698"
              />
            </div>

            <div className="edit-field edit-field--check">
              <label className="edit-check-label">
                <input
                  type="checkbox"
                  checked={editData.is_active}
                  onChange={(e) => setField("is_active")(e.target.checked)}
                />
                Активен
              </label>
            </div>

            <div className="edit-field edit-field--check">
              <label className="edit-check-label">
                <input
                  type="checkbox"
                  checked={editData.is_only_warehouse}
                  onChange={(e) => setField("is_only_warehouse")(e.target.checked)}
                />
                Только склад
              </label>
            </div>
          </div>

          <div className="edit-map-label">Выберите расположение на карте</div>
          <LocationPicker
            latitude={editData.latitude}
            longitude={editData.longitude}
            height="340px"
            onChange={(lat, lng) => {
              setEditData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
            }}
          />

          <div className="edit-actions">
            <button className="btn btn-save" onClick={handleEditSave} disabled={isSaving}>
              {isSaving ? "Сохранение…" : "Сохранить"}
            </button>
            <button className="btn btn-cancel" onClick={handleEditCancel} disabled={isSaving}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* ── INFO VIEW ── */}
      {!isEditing && storeStatus === "succeeded" && store && (
        <div className="store-info">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">ID:</span>
              <span className="info-value mono">{store.id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Название:</span>
              <span className="info-value">{store.name || "—"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Адрес:</span>
              <span className="info-value">{store.address || "—"}</span>
            </div>
            {store.latitude != null && store.longitude != null && (
              <div className="info-item">
                <span className="info-label">Координаты:</span>
                <span className="info-value mono">
                  {store.latitude.toFixed(6)}, {store.longitude.toFixed(6)}
                </span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Активен:</span>
              <span className="info-value">
                <span className={`badge ${store.is_active ? "badge-active" : "badge-inactive"}`}>
                  {store.is_active ? "Да" : "Нет"}
                </span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Только склад:</span>
              <span className="info-value">{store.is_only_warehouse ? "Да" : "Нет"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Создан:</span>
              <span className="info-value">{formatDateTime(store.created_at)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Обновлен:</span>
              <span className="info-value">{formatDateTime(store.update_at)}</span>
            </div>
          </div>
        </div>
      )}

      {!isEditing && storeStatus === "succeeded" && store && (
        <div className="store-map-section">
          <div className="section-title" style={{ padding: "16px 18px 8px" }}>
            Расположение
          </div>
          <div style={{ padding: "0 18px 18px" }}>
            <SingleStoreMap
              latitude={store.latitude}
              longitude={store.longitude}
              name={store.name}
              height="280px"
            />
          </div>
        </div>
      )}

      <div className="inventory-section">
        <div className="section-header">
          <div>
            <div className="section-title">Инвентарь склада</div>
            <div className="section-subtitle">
              Всего позиций: {inventoryCount} | Общее количество: {formatNumber(totalQuantity)}
            </div>
          </div>
          <div className="section-actions">
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Поиск: товар / штрихкод…"
              className="search"
            />
            <button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="btn">
              Обновить
            </button>
          </div>
        </div>

        {inventoryStatus === "loading" && <Loading text="Загрузка инвентаря…" />}
        {inventoryStatus === "failed" && <div className="error">{inventoryError}</div>}

        <div className="body">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">ID</th>
                  <th className="th">Товар</th>
                  <th className="th">Штрихкод</th>
                  <th className="th th-sortable" onClick={() => handleSort("quantity")}>
                    Количество{getSortIcon("quantity")}
                  </th>
                  <th className="th">Обновлен</th>
                </tr>
              </thead>
              <tbody>
                {visibleInventory.length === 0 && inventoryStatus === "succeeded" ? (
                  <tr>
                    <td colSpan={5} className="td-muted">
                      Инвентарь не найден
                    </td>
                  </tr>
                ) : (
                  visibleInventory.map((item) => {
                    const productName = item?.product?.name || item?.product_name || "—";
                    const productBarcode = item?.product?.barcode || item?.product_barcode || "—";
                    const quantity = Number(item?.quantity) || 0;
                    return (
                      <tr key={item.id} className="tr">
                        <td className="td-mono">{item.id}</td>
                        <td className="td">{productName}</td>
                        <td className="td-mono">{productBarcode}</td>
                        <td className={`td-mono ${quantity < 0 ? "td-negative" : ""}`}>
                          {formatNumber(quantity)}
                        </td>
                        <td className="td">{formatDateTime(item?.updated_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {inventoryCount > 0 && (
            <PaginationBar
              page={page}
              totalCount={inventoryCount}
              pageSize={PAGE_SIZE}
              itemsOnPage={visibleInventory.length}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}
